import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CheckCircle, XCircle, Play, Pause, Eye, Pen, Shield, UserCog, ArrowRight, Trash2, History
} from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { label: 'Critic', color: 'bg-red-600 text-white' },
  urgent: { label: 'Urgent', color: 'bg-orange-500 text-white' },
  high: { label: 'Ridicat', color: 'bg-amber-500 text-white' },
  medium: { label: 'Mediu', color: 'bg-yellow-400 text-slate-900' },
  low: { label: 'Scazut', color: 'bg-green-500 text-white' }
};

const TicketActionsCard = ({
  ticket, hasRole, hasPermission, onQuickAccept, onQuickStatusChange,
  onReject, onReassign, onTransform, onClose, onDelete, onRevise, onEditPartsEta
}) => {
  const isEditable = !['closed', 'transformed'].includes(ticket.status);
  const canWrite = hasPermission ? hasPermission('tickets.write') : hasRole('manager', 'admin', 'technician');
  const canAssign = hasPermission ? hasPermission('tickets.assign') : hasRole('manager', 'admin');
  const canClose = hasPermission ? hasPermission('tickets.close') : hasRole('manager', 'admin', 'technician');
  const canDelete = hasPermission ? hasPermission('tickets.delete') : hasRole('admin');

  return (
    <>
      {isEditable && (
        <Card className="border-[#0052CC]/20 hidden lg:block" data-testid="actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Actiuni</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {ticket.status === 'open' && canWrite && (
              <>
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700" onClick={onQuickAccept} data-testid="accept-ticket-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Accepta Tichet
                </Button>
                <Button variant="outline" className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50" onClick={onReject} data-testid="reject-ticket-btn">
                  <XCircle className="w-4 h-4 mr-2" /> Respinge
                </Button>
              </>
            )}

            {ticket.status === 'accepted' && canWrite && (
              <Button className="w-full justify-start bg-blue-600 hover:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')} data-testid="start-work-btn">
                <Play className="w-4 h-4 mr-2" /> Incepe Lucrul
              </Button>
            )}

            {ticket.status === 'in_progress' && canWrite && (
              <>
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')} data-testid="mark-resolved-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Rezolvat
                </Button>
                <Button variant="outline" className="w-full justify-start text-yellow-700 border-yellow-300 hover:bg-yellow-50" onClick={() => onQuickStatusChange('monitoring')} data-testid="monitoring-btn">
                  <Eye className="w-4 h-4 mr-2" /> Sub Monitorizare
                </Button>
                <Button variant="outline" className="w-full justify-start text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => onQuickStatusChange('waiting_parts')}>
                  <Pause className="w-4 h-4 mr-2" /> Asteapta Piese
                </Button>
              </>
            )}

            {ticket.status === 'monitoring' && canWrite && (
              <>
                <Button className="w-full justify-start bg-blue-600 hover:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')}>
                  <Play className="w-4 h-4 mr-2" /> Reia Lucrul
                </Button>
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Rezolvat
                </Button>
              </>
            )}

            {ticket.status === 'waiting_parts' && canWrite && (
              <>
                <Button className="w-full justify-start bg-blue-600 hover:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')}>
                  <Play className="w-4 h-4 mr-2" /> Reia Lucrul
                </Button>
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Rezolvat
                </Button>
                {onEditPartsEta && (
                  <Button variant="outline" className="w-full justify-start text-orange-700 border-orange-300 hover:bg-orange-50" onClick={onEditPartsEta} data-testid="edit-parts-eta-action-btn">
                    <Pause className="w-4 h-4 mr-2" /> Modifica Termen Piese
                  </Button>
                )}
              </>
            )}

            {ticket.status === 'resolved' && canClose && (
              <Button className="w-full justify-start bg-slate-700 hover:bg-slate-800" onClick={onClose} data-testid="close-ticket-btn">
                <Shield className="w-4 h-4 mr-2" /> Inchide cu Semnatura
              </Button>
            )}

            {['accepted', 'in_progress', 'waiting_parts', 'monitoring'].includes(ticket.status) && canClose && (
              <Button variant="outline" className="w-full justify-start text-slate-600" onClick={onClose}>
                <Pen className="w-4 h-4 mr-2" /> Inchide cu Semnatura
              </Button>
            )}

            {!['closed', 'transformed', 'rejected'].includes(ticket.status) && canAssign && (
              <Button variant="outline" className="w-full justify-start" onClick={onReassign} data-testid="reassign-ticket-btn">
                <UserCog className="w-4 h-4 mr-2" /> Reasigneaza
              </Button>
            )}

            {hasRole('manager', 'admin') && ['open', 'accepted'].includes(ticket.status) && (
              <Button variant="outline" className="w-full justify-start text-purple-600" onClick={onTransform} data-testid="transform-ticket-btn">
                <ArrowRight className="w-4 h-4 mr-2" /> Transforma in Revizie
              </Button>
            )}

            {canDelete && (
              <Button variant="outline" className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 mt-2" onClick={onDelete} data-testid="delete-ticket-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Sterge Tichet
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {ticket.status === 'closed' && canWrite && (
        <Card className="border-amber-200 hidden lg:block" data-testid="revision-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-amber-500" /> Revizuire
              {ticket.revision_count > 0 && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">{ticket.revision_count} revizuiri</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-start text-amber-700 border-amber-300 hover:bg-amber-50" onClick={onRevise} data-testid="revise-ticket-btn">
              <History className="w-4 h-4 mr-2" /> Revizuieste Tichetul
            </Button>
            <p className="text-xs text-slate-500">Permite corectarea materialelor, notelor sau fotografiilor. Toate modificarile sunt pastrate in istoric.</p>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default TicketActionsCard;
